/**
 * Geological Cross-Section (Figure 3)
 *
 * 2D vertical profile showing ground surface, geological formations
 * (Ogallala, Dockum, Permian), water table, and well screens.
 *
 * Formation thicknesses from Macrostrat/TWDB for Texas Panhandle:
 *   Ogallala: base at ~550ft below surface (at Dalhart)
 *   Dockum: ~600ft thick below Ogallala
 *   Permian: below Dockum
 */
import React, { useMemo, useState, useEffect } from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Scatter, ReferenceLine,
} from 'recharts';
import type { WellPoint } from '../../api/phreatic';
import { apiClient } from '../../api/client';

interface CrossSectionProps {
  wells: WellPoint[];
  centerLat: number;
  centerLon: number;
  aquifer?: string;
}

interface MacrostratFormation {
  name: string;
  base_depth_ft: number;
  color: string;
  lithology: string;
}

const FT_PER_DEG_LAT = 364000;
const FT_PER_DEG_LON = 308000;

// Fallback formation profiles (used when Macrostrat unavailable)
const FORMATION_PROFILES: Record<string, { name: string; color: string; opacity: number; base_depth_ft: number }[]> = {
  Ogallala: [
    { name: 'Ogallala Aquifer', color: '#d4a76a', opacity: 0.3, base_depth_ft: 550 },
    { name: 'Dockum Group', color: '#c0392b', opacity: 0.2, base_depth_ft: 1150 },
    { name: 'Permian', color: '#7f8c8d', opacity: 0.15, base_depth_ft: 2000 },
  ],
  Edwards: [
    { name: 'Edwards Limestone', color: '#bdc3c7', opacity: 0.3, base_depth_ft: 400 },
    { name: 'Glen Rose Formation', color: '#95a5a6', opacity: 0.2, base_depth_ft: 1000 },
    { name: 'Travis Peak', color: '#7f8c8d', opacity: 0.15, base_depth_ft: 1500 },
  ],
  other: [
    { name: 'Aquifer', color: '#d4a76a', opacity: 0.3, base_depth_ft: 300 },
    { name: 'Aquitard', color: '#95a5a6', opacity: 0.2, base_depth_ft: 600 },
    { name: 'Bedrock', color: '#7f8c8d', opacity: 0.15, base_depth_ft: 1000 },
  ],
};

export function GeologicalCrossSection({ wells, centerLat, centerLon, aquifer = 'other' }: CrossSectionProps) {
  // Fetch real geology from Macrostrat
  const [macrostratFormations, setMacrostratFormations] = useState<MacrostratFormation[] | null>(null);
  const [geoSource, setGeoSource] = useState<'loading' | 'macrostrat' | 'fallback'>('loading');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get('/phreatic/v4/geology', {
          params: { lat: centerLat, lon: centerLon },
        });
        if (cancelled) return;
        const fms = resp.data.formations;
        if (fms && fms.length > 0) {
          setMacrostratFormations(fms.map((f: any) => ({
            name: f.name,
            base_depth_ft: f.base_depth_ft,
            color: f.color,
            lithology: f.lithology,
          })));
          setGeoSource('macrostrat');
        } else {
          setGeoSource('fallback');
        }
      } catch {
        if (!cancelled) setGeoSource('fallback');
      }
    })();
    return () => { cancelled = true; };
  }, [centerLat, centerLon]);

  // Use Macrostrat data or fallback
  const formations = useMemo(() => {
    if (macrostratFormations && macrostratFormations.length > 0) {
      return macrostratFormations.map((f, i) => ({
        name: f.name,
        color: f.color,
        opacity: 0.3 - i * 0.03, // fade deeper layers
        base_depth_ft: f.base_depth_ft,
      }));
    }
    return FORMATION_PROFILES[aquifer] || FORMATION_PROFILES.other;
  }, [macrostratFormations, aquifer]);

  const data = useMemo(() => {
    const valid = wells.filter(w => w.latitude && w.longitude && w.ground_elevation_ft != null);
    if (valid.length < 2) return null;

    // Sort west to east
    const withDist = valid.map(w => ({
      well: w,
      dx: (w.longitude - centerLon) * FT_PER_DEG_LON,
    })).sort((a, b) => a.dx - b.dx);

    let cumDist = 0;
    return withDist.map((item, idx) => {
      if (idx > 0) {
        const prev = withDist[idx - 1];
        const ddx = (item.well.longitude - prev.well.longitude) * FT_PER_DEG_LON;
        const ddy = (item.well.latitude - prev.well.latitude) * FT_PER_DEG_LAT;
        cumDist += Math.sqrt(ddx * ddx + ddy * ddy);
      }

      const w = item.well;
      const ge = w.ground_elevation_ft!;
      const we = w.water_elevation_ft ?? (ge - (w.depth_to_water_ft ?? 0));
      const td = w.total_depth_ft ?? 200;

      const point: any = {
        distance_ft: Math.round(cumDist),
        label: w.well_name,
        ground_surface: Math.round(ge),
        water_table: Math.round(we),
        well_bottom: Math.round(ge - td),
        dtw: w.depth_to_water_ft ? Math.round(w.depth_to_water_ft) : null,
      };

      // Formation boundaries
      for (const fm of formations) {
        point[fm.name] = Math.round(ge - fm.base_depth_ft);
      }

      return point;
    });
  }, [wells, centerLat, centerLon, formations]);

  if (!data || data.length < 2) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center">
        <p className="text-mono-500 text-sm">Need at least 2 wells with elevation data for cross-section</p>
      </div>
    );
  }

  const allElevs = data.flatMap(d => {
    const vals = [d.ground_surface, d.water_table, d.well_bottom];
    for (const fm of formations) {
      if (d[fm.name] != null) vals.push(d[fm.name]);
    }
    return vals;
  });
  const minElev = Math.min(...allElevs) - 100;
  const maxElev = Math.max(...allElevs) + 50;

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
          Geological Cross-Section
          {geoSource === 'macrostrat' && <span className="ml-2 text-[9px] text-cyan-400 font-normal">USGS/MACROSTRAT</span>}
          {geoSource === 'loading' && <span className="ml-2 text-[9px] text-mono-500 font-normal">Loading geology...</span>}
        </h3>
        <div className="flex items-center gap-3 text-[9px] text-mono-400">
          <span><span className="inline-block w-3 h-2 rounded-sm mr-1" style={{ background: 'rgba(139,115,85,0.6)' }} />Ground</span>
          <span><span className="inline-block w-3 h-2 rounded-sm mr-1" style={{ background: 'rgba(96,165,250,0.5)' }} />Water Table</span>
          {formations.map(fm => (
            <span key={fm.name}><span className="inline-block w-3 h-2 rounded-sm mr-1" style={{ background: fm.color, opacity: fm.opacity + 0.3 }} />{fm.name}</span>
          ))}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart data={data} margin={{ top: 10, right: 20, bottom: 40, left: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
          <XAxis dataKey="distance_ft" tick={{ fill: '#888', fontSize: 10 }}
            label={{ value: 'Distance Along Section (ft)', position: 'insideBottom', offset: -25, fill: '#666', fontSize: 10 }} />
          <YAxis domain={[minElev, maxElev]} tick={{ fill: '#888', fontSize: 10 }}
            label={{ value: 'Elevation (ft MSL)', angle: -90, position: 'insideLeft', fill: '#666', fontSize: 10 }} />
          <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11, borderRadius: 6 }}
            labelFormatter={(val) => `${val} ft`} />

          {/* Formation layers (bottom up) */}
          {[...formations].reverse().map(fm => (
            <Area key={fm.name} type="monotone" dataKey={fm.name} name={fm.name}
              fill={fm.color} stroke={fm.color} fillOpacity={fm.opacity} strokeWidth={1} strokeDasharray="4 4" />
          ))}

          {/* Well bottom (total depth) */}
          <Area type="monotone" dataKey="well_bottom" name="Well Bottom"
            fill="none" stroke="#666" strokeWidth={1} strokeDasharray="2 2" />

          {/* Water table */}
          <Area type="monotone" dataKey="water_table" name="Water Table"
            fill="rgba(96, 165, 250, 0.2)" stroke="#60a5fa" strokeWidth={2.5} />

          {/* Ground surface */}
          <Area type="monotone" dataKey="ground_surface" name="Ground Surface"
            fill="rgba(139, 115, 85, 0.35)" stroke="#8B7355" strokeWidth={2.5} />

          {/* Well head markers */}
          <Scatter dataKey="ground_surface" fill="#fff" shape="diamond" />
        </ComposedChart>
      </ResponsiveContainer>

      {/* Well labels */}
      <div className="flex justify-around mt-1 text-[9px] text-mono-400">
        {data.map(d => (
          <div key={d.label} className="text-center">
            <div className="text-mono-300 font-medium">{d.label}</div>
            {d.dtw != null && <div>DTW: {d.dtw}ft</div>}
            <div className="text-mono-600">{d.ground_surface}ft MSL</div>
          </div>
        ))}
      </div>
    </div>
  );
}
